/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import VideoIcon from '@atlaskit/icon/core/video';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { convertVideoDuration } from '../convert-video-duration';
import { createChapterUrl } from '../create-chapter-url';
import { messages } from '../messages';

import type { ExternalChapter } from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isChapter } from './is-chapter';

const videoChapterStyles = cssMap({
	footerContainer: {
		paddingTop: token('space.150'),
		position: 'relative',
	},
	chapterLinkStyles: {
		color: token('color.link'),
		textDecoration: 'none',
		'&:hover': {
			color: token('color.link'),
			textDecoration: 'underline',
		},
		'&:active': {
			textDecoration: 'none',
		},
		'&:visited': {
			color: token('color.link.visited'),
		},
		'&:visited:active': {
			color: token('color.link.visited.pressed'),
		},
	},
	dropdownItemStyles: {
		display: 'flex',
		columnGap: token('space.025'),
		paddingBlock: token('space.050'),
		marginTop: token('space.100'),
	},

	seeAllStyles: {
		textDecoration: 'none',
		marginTop: token('space.150'),
		font: token('font.body.small'),
	},
});

const DropdownItems = ({
	chapters,
	url,
	getChapterUrl,
}: {
	chapters: ExternalChapter[];
	url: string;
	getChapterUrl?: (startTimeInSeconds: number, chapterIndex: number) => string;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{chapters.map(({ title, startTimeInSeconds }, chapterIndex) => {
				if (startTimeInSeconds === null || startTimeInSeconds === undefined) {
					return null;
				}

				return (
					<Box xcss={videoChapterStyles.dropdownItemStyles} key={`${title}-${startTimeInSeconds}`}>
						<VideoIcon
							label={formatMessage(messages.videoIconLabel)}
							size="medium"
							color={token('color.icon.accent.blue')}
						/>
						<Inline space={'space.050'}>
							<Text size="small" weight="medium">
								<Anchor
									xcss={videoChapterStyles.chapterLinkStyles}
									href={
										getChapterUrl?.(startTimeInSeconds, chapterIndex) ??
										createChapterUrl(url, startTimeInSeconds)
									}
									rel={'noreferrer'}
									target="_blank"
								>
									{convertVideoDuration(startTimeInSeconds)}
								</Anchor>
							</Text>
							<Text size="small">{title}</Text>
						</Inline>
					</Box>
				);
			})}
			{chapters.length > 10 && (
				<Box xcss={videoChapterStyles.seeAllStyles}>
					<Anchor href={url} target="_blank" xcss={videoChapterStyles.seeAllStyles}>
						{formatMessage(messages.seeAll)}
					</Anchor>
				</Box>
			)}
		</Box>
	);
};

const DropdownMenu = ({
	chapters,
	url,
	getChapterUrl,
	dropdownMessage: dropdownMessageOverride,
}: {
	chapters: ExternalChapter[];
	url: string;
	getChapterUrl?: (startTimeInSeconds: number, chapterIndex: number) => string;
	dropdownMessage?: string;
}) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const dropdownMessage =
		dropdownMessageOverride ??
		formatMessage(messages.dropdownMessage, {
			chapterCount: chapters.length,
		});

	return (
		<Box>
			<Button
				iconAfter={(iconProps) =>
					isExpanded ? (
						<ChevronDownIcon {...iconProps} size="small" />
					) : (
						<ChevronRightIcon {...iconProps} size="small" />
					)
				}
				onClick={() => setIsExpanded(!isExpanded)}
				isSelected={isExpanded}
			>
				{dropdownMessage}
			</Button>
			{isExpanded && <DropdownItems chapters={chapters} url={url} getChapterUrl={getChapterUrl} />}
		</Box>
	);
};

interface VideoChaptersProps {
	chapters?: ExternalChapter[] | null;
	url: string;
	getChapterUrl?: (startTimeInSeconds: number, chapterIndex: number) => string;
	dropdownMessage?: string;
}

/**
 * Shared video chapters component that displays a dropdown of chapter markers.
 * Used by video search results (Loom) to show chapter navigation.
 */
export const VideoChapters: ({
	chapters,
	url,
	getChapterUrl,
	dropdownMessage,
}: VideoChaptersProps) => JSX.Element | null = ({
	chapters,
	url,
	getChapterUrl,
	dropdownMessage,
}: VideoChaptersProps) => {
	if (!chapters || chapters.length === 0) {
		return null;
	}

	return (
		<Box xcss={videoChapterStyles.footerContainer}>
			<DropdownMenu
				chapters={chapters}
				url={url}
				getChapterUrl={getChapterUrl}
				dropdownMessage={dropdownMessage}
			/>
		</Box>
	);
};
